import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useWebSocketNotifications } from '@/hooks/useWebSocket';
import { MessageSquare, HelpCircle, Bell, ChevronDown, LogOut, User } from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearnerHeader = () => {
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);
  const userRef = useRef(null);

  const onNotification = useCallback((notif) => {
    setUnreadCount(prev => prev + 1);
    setNotifications(prev => [notif, ...prev].slice(0, 20));
  }, []);

  useWebSocketNotifications(onNotification);

  useEffect(() => {
    fetch(`${API}/notifications/unread-count`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(d => setUnreadCount(d.count))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifPanel(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const lastName = (user?.name || '').split(' ').pop()?.toUpperCase() || '';
  const firstName = (user?.name || '').split(' ').slice(0, -1).join(' ') || '';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-[72px] bg-white border-b border-slate-100 flex items-center justify-between px-5" data-testid="learner-header">
      {/* Left: Logo */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
          <span className="text-white font-bold text-sm">A</span>
        </div>
        <div>
          <p className="text-sm font-bold text-blue-700 leading-tight">Affea</p>
          <p className="text-[10px] text-slate-400 italic leading-tight">Formation et accompagnement</p>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Chat */}
        <button className="relative w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white hover:bg-blue-800 transition-colors" data-testid="header-chat-btn">
          <MessageSquare className="h-5 w-5" />
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button className="relative w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white hover:bg-blue-800 transition-colors"
            onClick={() => setShowNotifPanel(!showNotifPanel)} data-testid="header-notif-btn">
            <HelpCircle className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          {showNotifPanel && (
            <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="notif-panel">
              <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">Notifications</span>
                <button className="text-xs text-blue-600 hover:underline" onClick={() => { setUnreadCount(0); }}>Tout marquer lu</button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {notifications.length > 0 ? notifications.map((n, i) => (
                  <div key={i} className={cn("px-3 py-2.5 border-b border-slate-50 text-sm", !n.read && "bg-blue-50/50")}>
                    <p className="text-slate-700">{n.message || n.title}</p>
                  </div>
                )) : (
                  <p className="p-4 text-center text-sm text-slate-400">Aucune notification</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User dropdown */}
        <div className="relative" ref={userRef}>
          <button className="flex items-center gap-2 bg-blue-700 text-white rounded-full pl-3 pr-2 py-1.5 hover:bg-blue-800 transition-colors"
            onClick={() => setShowUserMenu(!showUserMenu)} data-testid="header-user-btn">
            <span className="text-sm font-medium">{lastName} {firstName}</span>
            <ChevronDown className="h-4 w-4" />
          </button>
          {showUserMenu && (
            <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="user-menu">
              <button className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {}}>
                <User className="h-4 w-4" />Mon profil
              </button>
              <button className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-slate-100"
                onClick={logout} data-testid="header-logout">
                <LogOut className="h-4 w-4" />Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default LearnerHeader;
